import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useSessionExpiration } from '../hooks/useSessionExpiration'
import axios from 'axios'
import toast from 'react-hot-toast'

interface CustomField {
  id: string
  name: string
  type: 'text' | 'number' | 'select' | 'textarea' | 'date'
  required: boolean
  options?: string[]
  defaultValue?: string
  sortOrder: number
}

interface AdvancedTicketForm {
  title: string
  description: string
  priorityId: string
  categoryId: string
  departmentId: string
  sourceId?: string
  dueDate: string
  serviceType: string
  estimatedHours: number
  tags: string[]
  customFields: { [key: string]: any }
  clientOrgId: string
  clientUserId: string
  assignedTo: string
}

const AdvancedTicketCreation: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { handleAuthError, trackLogin } = useSessionExpiration()
  
  const [formData, setFormData] = useState<AdvancedTicketForm>({
    title: '',
    description: '',
    priorityId: '',
    categoryId: '',
    departmentId: '',
    dueDate: '',
    serviceType: '',
    estimatedHours: 0,
    tags: [],
    customFields: {},
    clientOrgId: '',
    clientUserId: '',
    assignedTo: ''
  })
  
  const [priorities, setPriorities] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [departments, setDepartments] = useState<any[]>([])
  const [sources, setSources] = useState<any[]>([])
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const [clientOrganizations, setClientOrganizations] = useState<any[]>([])
  const [clientUsers, setClientUsers] = useState<any[]>([])
  const [staffUsers, setStaffUsers] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [newTag, setNewTag] = useState('')

  // Track login time to prevent false session expiration
  useEffect(() => {
    trackLogin()
  }, [trackLogin])

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const [prioritiesRes, categoriesRes, departmentsRes, sourcesRes, orgsRes, usersRes] = await Promise.all([
          axios.get('/api/tickets/priorities'),
          axios.get('/api/tickets/categories'),
          axios.get('/api/tickets/departments'),
          axios.get('/api/tickets/sources'),
          axios.get('/api/organizations'),
          axios.get('/api/users')
        ])

        if (prioritiesRes.data.success) setPriorities(prioritiesRes.data.data)
        if (categoriesRes.data.success) setCategories(categoriesRes.data.data)
        if (departmentsRes.data.success) setDepartments(departmentsRes.data.data)
        if (sourcesRes.data.success) setSources(sourcesRes.data.data)
        if (orgsRes.data.success) setClientOrganizations(orgsRes.data.data)
        if (usersRes.data.success) {
          // Filter for staff users only
          const staffUsers = usersRes.data.data.filter((user: any) => 
            user.roles.some((userRole: any) => 
              ['administrator', 'support_staff', 'manager', 'staff'].includes(userRole.role.name)
            )
          )
          setStaffUsers(staffUsers)
        }
      } catch (error) {
        console.error('Error fetching form data:', error)
        
        // Check if it's a session expiration error
        if (handleAuthError(error)) {
          return // Session expired modal will be shown
        }
        
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
        console.error('Specific error details:', errorMessage)
        toast.error(`Failed to load form data: ${errorMessage}`)
      }
    }

    fetchFormData()
  }, [])

  useEffect(() => {
    if (formData.serviceType) {
      fetchCustomFields(formData.serviceType)
    }
  }, [formData.serviceType])

  // Fetch client users when client organization is selected
  useEffect(() => {
    const fetchClientUsers = async () => {
      if (!formData.clientOrgId) {
        setClientUsers([])
        setFormData(prev => ({ ...prev, clientUserId: '' }))
        return
      }

      try {
        const response = await axios.get(`/api/organizations/${formData.clientOrgId}/users`)
        if (response.data.success) {
          setClientUsers(response.data.data || [])
        }
      } catch (error) {
        console.error('Error fetching client users:', error)
        setClientUsers([])
      }
    }

    fetchClientUsers()
  }, [formData.clientOrgId])

  const fetchCustomFields = async (serviceType: string) => {
    try {
      const response = await axios.get(`/api/advanced-tickets/custom-fields/${serviceType}`)
      if (response.data.success) {
        setCustomFields(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error)
      // Custom fields are optional, so don't show error
    }
  }

  const handleInputChange = (field: keyof AdvancedTicketForm, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleCustomFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [fieldName]: value
      }
    }))
  }

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.title || !formData.description || !formData.priorityId || !formData.categoryId || !formData.departmentId || !formData.clientOrgId || !formData.clientUserId) {
      toast.error('Please fill in all required fields')
      return
    }

    setIsLoading(true)
    
    try {
      const response = await axios.post('/api/advanced-tickets/with-custom-fields', formData)
      
      if (response.data.success) {
        toast.success('Advanced ticket created successfully!')
        navigate(`/tickets/${response.data.data.id}`)
      } else {
        toast.error(response.data.error || 'Failed to create ticket')
      }
    } catch (error: any) {
      console.error('Error creating ticket:', error)
      toast.error(error.response?.data?.error || 'Failed to create ticket')
    } finally {
      setIsLoading(false)
    }
  }

  const renderCustomField = (field: CustomField) => {
    const value = formData.customFields[field.name] || field.defaultValue || ''

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="input-field"
            placeholder={field.name}
            required={field.required}
          />
        )
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="input-field"
            rows={3}
            placeholder={field.name}
            required={field.required}
          />
        )
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, Number(e.target.value))}
            className="input-field"
            placeholder={field.name}
            required={field.required}
          />
        )
      
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="input-field"
            required={field.required}
          >
            <option value="">Select {field.name}</option>
            {field.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        )
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="input-field"
            required={field.required}
          />
        )
      
      default:
        return null
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Create Advanced Ticket</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create a comprehensive ticket with custom fields, tags, and enhanced metadata
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="input-field mt-1"
                placeholder="Enter ticket title"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Service Type</label>
              <select
                value={formData.serviceType}
                onChange={(e) => handleInputChange('serviceType', e.target.value)}
                className="input-field mt-1"
              >
                <option value="">Select service type</option>
                <option value="smm">Social Media Management</option>
                <option value="website">Website Development</option>
                <option value="seo">SEO Services</option>
                <option value="design">Graphic Design</option>
                <option value="consulting">Consulting</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Priority *</label>
              <select
                value={formData.priorityId}
                onChange={(e) => handleInputChange('priorityId', e.target.value)}
                className="input-field mt-1"
                required
              >
                <option value="">Select priority</option>
                {priorities.map(priority => (
                  <option key={priority.id} value={priority.id}>
                    {priority.displayName || priority.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Category *</label>
              <select
                value={formData.categoryId}
                onChange={(e) => handleInputChange('categoryId', e.target.value)}
                className="input-field mt-1"
                required
              >
                <option value="">Select category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Department *</label>
              <select
                value={formData.departmentId}
                onChange={(e) => handleInputChange('departmentId', e.target.value)}
                className="input-field mt-1"
                required
              >
                <option value="">Select department</option>
                {departments.map(department => (
                  <option key={department.id} value={department.id}>{department.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Method of Contact</label>
              <select
                value={formData.sourceId || ''}
                onChange={(e) => handleInputChange('sourceId', e.target.value || undefined)}
                className="input-field mt-1"
              >
                <option value="">Select Method of Contact</option>
                {sources.map(source => (
                  <option key={source.id} value={source.id}>{source.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Due Date</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Client Organization *</label>
              <select
                value={formData.clientOrgId}
                onChange={(e) => {
                  handleInputChange('clientOrgId', e.target.value)
                  handleInputChange('clientUserId', '') // Reset client user when org changes
                }}
                className="input-field mt-1"
                required
              >
                <option value="">Select client organization</option>
                {clientOrganizations
                  .filter(org => org.isActive)
                  .map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Client User (Caller) *</label>
              <select
                value={formData.clientUserId}
                onChange={(e) => handleInputChange('clientUserId', e.target.value)}
                className="input-field mt-1"
                required
                disabled={!formData.clientOrgId}
              >
                <option value="">Select client user</option>
                {clientUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Assign To Staff Member</label>
              <select
                value={formData.assignedTo}
                onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                className="input-field mt-1"
              >
                <option value="">Unassigned (No Staff Member)</option>
                {staffUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Description</h2>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="input-field w-full"
            rows={6}
            placeholder="Describe the issue or request in detail..."
            required
          />
        </div>

        {/* Enhanced Metadata */}
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Enhanced Metadata</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Estimated Hours</label>
              <input
                type="number"
                value={formData.estimatedHours}
                onChange={(e) => handleInputChange('estimatedHours', Number(e.target.value))}
                className="input-field mt-1"
                placeholder="0"
                min="0"
                step="0.5"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Tags</label>
              <div className="mt-1 flex space-x-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  className="input-field flex-1"
                  placeholder="Add tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="btn-secondary"
                >
                  Add
                </button>
              </div>
              
              {formData.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:text-blue-600 hover:bg-blue-200"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Custom Fields */}
        {customFields.length > 0 && (
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Custom Fields</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {customFields.map(field => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-gray-700">
                    {field.name} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <div className="mt-1">
                    {renderCustomField(field)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            type="button"
            onClick={() => navigate('/tickets')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Advanced Ticket'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdvancedTicketCreation
